import { shallowReactive } from 'vue';
import { ui } from './editor/bridge';
import { pickSvgFiles } from './editor/clipboard';
import { template } from './editor/diagram';
import { changelog, showChangelog } from './changelog';
import { feedback } from './feedback';
import { t } from './i18n';
import type { useApp } from './stores/app';

/**
 * Platform tools of the feed's Tools section. A new tool of the platform is one entry in BUILTIN
 * (or `registerFeedTool` from code that owns it); tools of modules come from their manifests
 * (`moduleFeedTools` in modules.ts) and are shown after these.
 */
export interface FeedToolContext {
  app: ReturnType<typeof useApp>;
  /** A canvas is open in the editor. */
  canvas: boolean;
  readOnly: boolean;
}

export interface PlatformFeedTool {
  id: string;
  /** Platform icon name (Icon.vue). */
  icon: string;
  label: () => string;
  hint?: () => string;
  /** Shown only when this is true (default: always). */
  when?: (ctx: FeedToolContext) => boolean;
  /** What the tool opened is on screen: the tool is highlighted and a press runs `run` again to close it. */
  active?: () => boolean;
  /** Opens (or, when `active`, closes) what the tool is for. */
  run: (ctx: FeedToolContext) => void;
}

const onCanvas = (ctx: FeedToolContext) => ctx.canvas;
const canEdit = (ctx: FeedToolContext) => ctx.canvas && !ctx.readOnly;
const offCanvas = (ctx: FeedToolContext) => !ctx.canvas;

const BUILTIN: PlatformFeedTool[] = [
  { id: 'palette', icon: 'command', label: () => t('feed.tool.palette'), hint: () => t('feed.tool.paletteHint'), when: onCanvas, active: () => ui.paletteOpen, run: () => (ui.paletteOpen = !ui.paletteOpen) },
  { id: 'find', icon: 'search', label: () => t('search.title'), hint: () => t('feed.tool.findHint'), when: onCanvas, active: () => ui.searchOpen, run: () => (ui.searchOpen = !ui.searchOpen) },
  { id: 'bookmarks', icon: 'bookmark', label: () => t('bookmarks.title'), when: onCanvas, active: () => ui.bookmarksOpen, run: () => (ui.bookmarksOpen = !ui.bookmarksOpen) },
  {
    id: 'diagram',
    icon: 'diagram',
    label: () => t('diagram.tool'),
    hint: () => t('feed.tool.diagramHint'),
    when: canEdit,
    active: () => !!ui.diagramEdit && ui.diagramEdit.id === null,
    run: () => (ui.diagramEdit = ui.diagramEdit?.id === null ? null : { id: null, source: template('flow') }),
  },
  { id: 'import-svg', icon: 'upload', label: () => t('canvas.importSvg'), when: canEdit, run: () => pickSvgFiles() },
  { id: 'shortcuts', icon: 'keyboard', label: () => t('keys.title'), when: onCanvas, active: () => ui.keysOpen, run: () => (ui.keysOpen = !ui.keysOpen) },
  { id: 'new-canvas', icon: 'plus', label: () => t('home.newCanvas'), when: offCanvas, run: (ctx) => void ctx.app.createCanvas() },
  { id: 'modules', icon: 'apps', label: () => t('app.modules'), when: offCanvas, run: (ctx) => { ui.chatOpen = false; ctx.app.go('modules'); } },
  { id: 'whats-new', icon: 'info', label: () => t('changelog.menu'), when: () => changelog.entries.length > 0, run: () => showChangelog() },
  { id: 'feedback', icon: 'comment', label: () => t('feedback.menu'), when: () => !!feedback.email, active: () => feedback.open, run: () => (feedback.open = !feedback.open) },
];

const registered = shallowReactive<PlatformFeedTool[]>([]);

/** Add a platform tool from the code that owns it; returns a function that removes it. */
export function registerFeedTool(tool: PlatformFeedTool): () => void {
  registered.push(tool);
  return () => {
    const i = registered.indexOf(tool);
    if (i >= 0) registered.splice(i, 1);
  };
}

/** Platform tools that apply right now, in order. */
export function platformFeedTools(ctx: FeedToolContext): PlatformFeedTool[] {
  return [...BUILTIN, ...registered].filter((tool) => !tool.when || tool.when(ctx));
}
